"use client";

import { useT } from "@/lib/i18n/context";
import type { WJobOffersContent } from "@/lib/api/cms-window-content";
import { wJobOffersTranslations } from "./WJobOffers.i18n";
import styles from "./WJobOffers.module.css";

/**
 * WJobOffers - widget listy ofert pracy.
 *
 * Karty ofert (tytul + 3 pigulki meta + krotki opis + pigulki technologii)
 * z przyciskiem "Aplikuj" po prawej. Kliknięcie w przycisk otwiera mailto:
 * na adres z config[apply_email] (jeden adres dla wszystkich ofert).
 *
 * Dane z `W_JOB_OFFERS_CONTROLLER::getApiContent()` - czyta z komponentu
 * `c_job_offers` calej domeny+jezyka (nie tylko jeden katalog).
 */
export function WJobOffers({ content }: WJobOffersProps) {
  const t = useT(wJobOffersTranslations);

  if (!content || (content.items.length === 0 && !content.cta)) return null;

  return (
    <section className={styles.root}>
      <div className={styles.inner}>
        {content.heading && (
          <h2 className={styles.heading}>{content.heading}</h2>
        )}

        <ul className={styles.list}>
          {content.items.map((item) => {
            const pills = [item.team, item.employment_type, item.work_mode].filter(
              (p): p is string => !!p,
            );

            return (
              <li key={item.id} className={styles.card}>
                <div className={styles.body}>
                  <h3 className={styles.cardTitle}>{item.name}</h3>

                  {pills.length > 0 && (
                    <ul className={styles.pillList}>
                      {pills.map((p, i) => (
                        <li key={i} className={styles.pill}>
                          {p}
                        </li>
                      ))}
                    </ul>
                  )}

                  {item.short_desc && (
                    <p className={styles.cardDesc}>{item.short_desc}</p>
                  )}

                  {item.technologies.length > 0 && (
                    <ul className={styles.techList}>
                      {item.technologies.map((tech, i) => (
                        <li key={i} className={styles.tech}>
                          {tech}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {content.apply_href && (
                  <div className={styles.applyWrap}>
                    <a
                      href={`${content.apply_href}?subject=${encodeURIComponent(item.name)}`}
                      className={styles.applyBtn}
                    >
                      {t.apply} <span aria-hidden="true">&#8594;</span>
                    </a>
                  </div>
                )}
              </li>
            );
          })}

          {content.cta && (
            <li className={styles.ctaCard}>
              <div className={styles.body}>
                <h3 className={styles.ctaHeading}>{content.cta.heading}</h3>
                {content.cta.text && (
                  <p className={styles.cardDesc}>{content.cta.text}</p>
                )}
              </div>

              {content.apply_href && (
                <div className={styles.applyWrap}>
                  <a
                    href={content.apply_href}
                    className={styles.ctaBtn}
                  >
                    {content.cta.button_label ?? t.sendCv}
                  </a>
                </div>
              )}
            </li>
          )}
        </ul>
      </div>
    </section>
  );
}

export type WJobOffersProps = {
  content: WJobOffersContent | null;
};
